
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import usePromptCard from "@/hooks/usePromptCard";
import PromptCardHeader from "./PromptCardHeader";
import PromptContent from "./PromptContent";
import PromptAuthor from "./PromptAuthor";
import PromptActions from "./PromptActions";
import { MessageCircle, Heart } from "lucide-react";
import SharePromptButton from "./SharePromptButton";

export interface PromptCardProps {
  prompt: {
    id: string;
    title: string;
    description: string;
    content: string;
    category: string;
    category_id?: string;
    likes?: number;
    reviews?: number;
    copies?: number;
    author?: {
      id: string;
      name: string;
    };
    createdAt?: string;
  };
  showContent?: boolean;
  showActions?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent, id: string) => void;
}

const PromptCard = ({
  prompt,
  showContent = false,
  showActions = false,
  className = "",
  onClick,
}: PromptCardProps) => {
  const navigate = useNavigate();
  const {
    isCopied,
    handleCopyContent: handleCopy,
    toggleLike: handleLikeToggle,
    isLiked,
    copyCount,
  } = usePromptCard({ 
    id: prompt.id, 
    content: prompt.content, 
    initialLikes: prompt.likes, 
    initialCopies: prompt.copies 
  });
  
  const handleCardClick = (e: React.MouseEvent) => {
    // Check if click originated from ActionButton elements (like save/collection buttons)
    // We can identify them by checking if their parent elements have stopPropagation attribute
    if ((e.target as HTMLElement).closest('[data-stop-propagation="true"]')) {
      return;
    }
    
    if (onClick) {
      onClick(e, prompt.id);
    } else {
      // Navigate to the prompt details page when card is clicked
      navigate(`/prompt/${prompt.id}`);
    }
  };
  
  return (
    <div 
      className={`
        bg-background rounded-lg border shadow-sm p-4 transition-all 
        hover:shadow-md hover:border-muted cursor-pointer
        ${className}
      `}
      onClick={handleCardClick}
    >
      <PromptCardHeader 
        id={prompt.id}
        title={prompt.title}
        description={prompt.description}
        category={prompt.category}
        category_id={prompt.category_id}
        copyCount={copyCount}
        isCopied={isCopied}
        onCopy={handleCopy}
        onClick={(e) => e.stopPropagation()} // Prevent navigation when clicking on header elements
        isLiked={isLiked}
        likeCount={prompt.likes}
        reviewCount={prompt.reviews}
        onToggleLike={handleLikeToggle}
      />
      
      {showContent && (
        <PromptContent content={prompt.content} />
      )}
      
      <div className="mt-4 space-y-4">
        {prompt.author && prompt.createdAt && (
          <PromptAuthor 
            id={prompt.author.id} 
            name={prompt.author.name} 
            createdAt={prompt.createdAt}
          />
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <button 
              onClick={(e) => {
                e.stopPropagation(); // Prevent card navigation when clicking like button
                handleLikeToggle(e);
              }}
              className={`flex items-center gap-1 hover:text-primary transition-colors ${
                isLiked ? 'text-primary' : ''
              }`}
              data-stop-propagation="true"
            >
              <Heart className="h-4 w-4" fill={isLiked ? "currentColor" : "none"} />
              <span>{prompt.likes || 0}</span>
            </button>
            
            <div className="flex items-center gap-1">
              <MessageCircle className="h-4 w-4" />
              <span>{prompt.reviews || 0}</span>
            </div>

            <div data-stop-propagation="true">
              <SharePromptButton 
                promptId={prompt.id} 
                title={prompt.title}
                description={prompt.description}
                variant="ghost"
                size="sm"
                className="p-0 h-auto hover:bg-transparent"
              />
            </div>
          </div>
        </div>
      </div>
      
      {showActions && (
        <PromptActions 
          id={prompt.id} 
          title={prompt.title}
          content={prompt.content}
          isCopied={isCopied}
          onCopy={(e) => {
            e.stopPropagation(); // Prevent card navigation when copying
            handleCopy(e);
          }}
          category={prompt.category}
        />
      )}
    </div>
  );
};

export default PromptCard;
